import type { GameScene } from "../engine/SceneManager";
import type { Game } from "../engine/Game";
import type { Scene } from "@babylonjs/core/scene";
import { h, clear } from "../ui/dom";
import { newDesign } from "../parts/Rocket";
import { showModal } from "../ui/Modal";

export class MainMenuScene implements GameScene {
  readonly babylonScene: Scene | null = null;
  private uiEl?: HTMLElement;

  constructor(readonly game: Game) {}

  async enter() {
    const root = this.game.uiRoot;
    clear(root);

    const menu = h("div", { class: "menu" }, [
      h("div", { class: "title" }, ["COSMO KIDS"]),
      h("div", { class: "subtitle muted" }, ["A space flight simulator"]),
      h("div", { class: "actions" }, [
        h("button", { class: "btn primary", onclick: () => this.onNew() }, ["New Rocket"]),
        h("button", { class: "btn", onclick: () => this.onContinue() }, ["Continue"]),
        h("button", { class: "btn", onclick: () => this.onAchievements() }, ["Achievements"]),
        h("button", { class: "btn", onclick: () => this.onAbout() }, ["About"]),
      ]),
      h("div", { class: "muted", style: "position:absolute;bottom:20px;left:0;right:0;text-align:center;font-size:12px" }, [
        "Designed for iPad · Touch to build, drag to steer",
        h("br", {}, []),
        h("span", { style: "opacity:0.6;font-size:10px" }, [`build ${__BUILD_ID__}`]),
      ]),
    ]);
    root.appendChild(menu);
    this.uiEl = menu;
  }

  private async onNew() {
    const design = newDesign("My Rocket");
    await this.game.sceneManager.go("builder", { design });
  }

  private async onContinue() {
    const all = await this.game.saveManager.listRockets();
    if (all.length === 0) {
      showModal(this.game.uiRoot, "No saved rockets yet", "Build and save your first rocket from the hangar.");
      return;
    }
    all.sort((a, b) => b.updatedAt - a.updatedAt);

    const list = h("div", {}, []);
    for (const d of all) {
      const row = h("div", { style: "display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid var(--line);gap:12px" }, [
        h("div", {}, [
          h("div", { style: "font-weight:600" }, [d.name]),
          h("div", { class: "muted", style: "font-size:12px" }, [
            `${d.parts.length} part${d.parts.length === 1 ? "" : "s"} · ${new Date(d.updatedAt).toLocaleDateString()}`,
          ]),
        ]),
        h("div", { style: "display:flex;gap:8px" }, [
          h("button", { class: "btn", onclick: async () => {
            close();
            await this.game.sceneManager.go("builder", { design: d });
          } }, ["Edit"]),
          h("button", { class: "btn primary", onclick: async () => {
            close();
            await this.game.sceneManager.go("flight", { designId: d.id });
          } }, ["Launch"]),
        ]),
      ]);
      list.appendChild(row);
    }

    const close = showModal(this.game.uiRoot, "My Rockets", list, [
      { label: "Close", onClick: () => {} },
    ]);
  }

  private onAchievements() {
    const list = h("div", {}, []);
    for (const a of this.game.achievements.list()) {
      list.appendChild(
        h("div", { style: `display:flex;justify-content:space-between;padding:10px 0;border-bottom:1px solid var(--line);opacity:${a.unlocked ? 1 : 0.45}` }, [
          h("div", {}, [
            h("div", { style: "font-weight:600" }, [`${a.unlocked ? "🏆 " : "🔒 "}${a.title}`]),
            h("div", { class: "muted", style: "font-size:12px" }, [a.description]),
          ]),
        ]),
      );
    }
    showModal(this.game.uiRoot, "Achievements", list, [{ label: "Close", onClick: () => {} }]);
  }

  private onAbout() {
    showModal(
      this.game.uiRoot,
      "About",
      h("div", {}, [
        h("p", {}, ["Build rockets, launch them, and fly to the Moon, Mars, and beyond."]),
        h("p", { class: "muted" }, ["Designed for 9–12 year olds. Tap to select, drag to place, pinch to zoom."]),
        h("p", { class: "muted" }, ["v0.1 — alpha build."]),
      ]),
    );
  }

  leave() {}
  dispose() { this.uiEl?.remove(); }
}
